import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
} from "@mui/material";
import { getAuditLogs, getAuditFilters } from "../store/auditLog/action";

const PAGE_LIMIT = 20;

const actionColor = (action) => {
  switch (action) {
    case "Create":
      return "success";
    case "Update":
      return "info";
    case "Delete":
      return "error";
    case "Toggle Status":
      return "warning";
    default:
      return "default";
  }
};

const AuditHistory = () => {
  const dispatch = useDispatch();
  const { logs, total, modules, actions } = useSelector(
    (state) => state.auditLog
  );

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filterModule, setFilterModule] = useState("ALL");
  const [filterAction, setFilterAction] = useState("ALL");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchLogs = useCallback(() => {
    dispatch(
      getAuditLogs({
        start: page,
        limit: PAGE_LIMIT,
        search: search || "ALL",
        module: filterModule,
        action: filterAction,
        startDate: startDate || "ALL",
        endDate: endDate || "ALL",
      })
    );
  }, [dispatch, page, search, filterModule, filterAction, startDate, endDate]);

  useEffect(() => {
    dispatch(getAuditFilters());
  }, [dispatch]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const totalPages = Math.ceil(total / PAGE_LIMIT) || 1;

  const handleReset = () => {
    setSearch("");
    setFilterModule("ALL");
    setFilterAction("ALL");
    setStartDate("");
    setEndDate("");
    setPage(1);
  };

  const openDetail = (log) => {
    setSelectedLog(log);
    setDetailOpen(true);
  };

  const renderJson = (data, label) => {
    if (!data || (typeof data === "object" && Object.keys(data).length === 0))
      return null;
    return (
      <div style={{ marginBottom: 12 }}>
        <strong>{label}:</strong>
        <pre
          style={{
            background: "rgba(255,255,255,0.08)",
            color: "#e0e0e0",
            padding: 10,
            borderRadius: 6,
            maxHeight: 300,
            overflow: "auto",
            fontSize: 12,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {JSON.stringify(data, null, 2)}
        </pre>
      </div>
    );
  };

  return (
    <>
      <div className="page-title">
        <div className="row">
          <div className="col-12 col-md-6 order-md-1 order-last">
            <h3 style={{ color: "#e04f67" }}>Audit History</h3>
          </div>
          <div className="col-12 col-md-6 order-md-2 order-first">
            <nav
              aria-label="breadcrumb"
              className="breadcrumb-header float-start float-lg-end"
            >
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a href="/admin/dashboard">Dashboard</a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Audit History
                </li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col">
          <div className="card" id="card">
            <div className="card-header pb-0">
              <div className="row align-items-center">
                <div className="col-xs-12 col-sm-12 col-md-6 col-lg-8 float-left">
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by name, email, route..."
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                      style={{ maxWidth: 250 }}
                    />

                    <select
                      className="form-select"
                      value={filterModule}
                      onChange={(e) => {
                        setFilterModule(e.target.value);
                        setPage(1);
                      }}
                      style={{ maxWidth: 170 }}
                    >
                      <option value="ALL">All Modules</option>
                      {modules.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>

                    <select
                      className="form-select"
                      value={filterAction}
                      onChange={(e) => {
                        setFilterAction(e.target.value);
                        setPage(1);
                      }}
                      style={{ maxWidth: 150 }}
                    >
                      <option value="ALL">All Actions</option>
                      {actions.map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </select>

                    <input
                      type="date"
                      className="form-control"
                      value={startDate}
                      onChange={(e) => {
                        setStartDate(e.target.value);
                        setPage(1);
                      }}
                      style={{ maxWidth: 150 }}
                    />
                    <span>to</span>
                    <input
                      type="date"
                      className="form-control"
                      value={endDate}
                      onChange={(e) => {
                        setEndDate(e.target.value);
                        setPage(1);
                      }}
                      style={{ maxWidth: 150 }}
                    />

                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={handleReset}
                    >
                      Reset
                    </button>
                  </div>
                </div>
                <div className="col-xs-12 col-sm-12 col-md-6 col-lg-4 float-right mt-3 mt-lg-0">
                  <p className="text-muted mb-0 text-end">
                    Total: <strong>{total}</strong> logs
                  </p>
                </div>
              </div>
            </div>

            <div className="card-body card-overflow pt-0">
              <table className="table table-striped mt-5">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Date / Time</th>
                    <th>User</th>
                    <th>Module</th>
                    <th>Action</th>
                    <th>Description</th>
                    <th>Target ID</th>
                    <th>IP</th>
                    <th style={{ width: 60 }}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-4">
                        No audit logs found.
                      </td>
                    </tr>
                  ) : (
                    logs.map((log, idx) => (
                      <tr key={log._id}>
                        <td>{(page - 1) * PAGE_LIMIT + idx + 1}</td>
                        <td style={{ whiteSpace: "nowrap", fontSize: 13 }}>
                          {dayjs(log.createdAt).format("DD MMM YYYY")}
                          <br />
                          <small className="text-muted">
                            {dayjs(log.createdAt).format("hh:mm:ss A")}
                          </small>
                        </td>
                        <td>
                          <span style={{ fontWeight: 500 }}>
                            {log.performerName}
                          </span>
                          <br />
                          <small className="text-muted">
                            {log.performerEmail}
                          </small>
                          <br />
                          <small
                            style={{
                              fontSize: 11,
                              color:
                                log.performerModel === "Admin"
                                  ? "#e04f67"
                                  : "#1976d2",
                            }}
                          >
                            {log.performerModel}
                          </small>
                        </td>
                        <td>
                          <Chip
                            label={log.module}
                            size="small"
                            variant="outlined"
                            style={{ fontSize: 12, color: "#000000", borderColor: "#888" }}
                          />
                        </td>
                        <td>
                          <Chip
                            label={log.action}
                            size="small"
                            color={actionColor(log.action)}
                            style={{ fontSize: 12 }}
                          />
                        </td>
                        <td
                          style={{
                            maxWidth: 250,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: 13,
                          }}
                          title={log.description}
                        >
                          {log.description || "-"}
                        </td>
                        <td style={{ fontSize: 11, fontFamily: "monospace", color: "#90caf9" }}>
                          {log.targetId || "-"}
                        </td>
                        <td style={{ fontSize: 13 }}>{log.ipAddress || "-"}</td>
                        <td>
                          <IconButton
                            size="small"
                            onClick={() => openDetail(log)}
                            title="View Details"
                          >
                            <i
                              className="fas fa-eye"
                              style={{ fontSize: 14, color: "#e04f67" }}
                            ></i>
                          </IconButton>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center align-items-center mt-3 gap-2">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    &laquo; Prev
                  </button>
                  <span>
                    Page {page} of {totalPages}
                  </span>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next &raquo;
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            color: "#000000",
          }}
        >
          <span>Audit Log Detail</span>
          <IconButton size="small" onClick={() => setDetailOpen(false)}>
            <i className="fas fa-times"></i>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ color: "#000000" }}>
          {selectedLog && (
            <div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <p>
                    <strong>Performed By:</strong> {selectedLog.performerName} (
                    {selectedLog.performerEmail})
                  </p>
                  <p>
                    <strong>Type:</strong>{" "}
                    <Chip
                      label={selectedLog.performerModel}
                      size="small"
                      color={
                        selectedLog.performerModel === "Admin"
                          ? "error"
                          : "primary"
                      }
                      style={{ fontSize: 12 }}
                    />
                  </p>
                  <p>
                    <strong>Module:</strong> {selectedLog.module}
                  </p>
                  <p>
                    <strong>Action:</strong>{" "}
                    <Chip
                      label={selectedLog.action}
                      size="small"
                      color={actionColor(selectedLog.action)}
                    />
                  </p>
                </div>
                <div className="col-md-6">
                  <p>
                    <strong>Date/Time:</strong>{" "}
                    {dayjs(selectedLog.createdAt).format(
                      "DD MMM YYYY, hh:mm:ss A"
                    )}
                  </p>
                  <p>
                    <strong>IP Address:</strong> {selectedLog.ipAddress || "-"}
                  </p>
                  <p>
                    <strong>Route:</strong>{" "}
                    <code style={{ fontSize: 12, color: "#ccc" }}>{selectedLog.route}</code>
                  </p>
                  {selectedLog.targetId && (
                    <p>
                      <strong>Target ID:</strong>{" "}
                      <code style={{ fontSize: 12, color: "#90caf9" }}>{selectedLog.targetId}</code>
                    </p>
                  )}
                  <p>
                    <strong>Description:</strong>{" "}
                    {selectedLog.description || "-"}
                  </p>
                </div>
              </div>

              <hr />

              {renderJson(selectedLog.requestData, "Request Data (Changes Submitted)")}
              {renderJson(selectedLog.oldData, "Previous Data (Before Change)")}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AuditHistory;
